/**
 * Projects — the real list, `GET /organizations/{id}/projects` (item AO).
 *
 * A port of `bimgo-ai-web`'s `ProjectsSection` + `SearchAndFilterBar` +
 * `ProjectsList`, kept deliberately close to it: the same section heading and
 * project count, the same search placeholder, the same four states with the
 * same wording, the same table columns, and the same per-row hamburger menu
 * (Edit, Members, Delete). Style follows the web client; layout follows this
 * shell, per the standing split.
 *
 * Two things are genuinely this client's and have no web equivalent: clicking a
 * row *binds* the project for chat as well as opening it, and the bound row
 * carries a "Selected" tag so the binding is visible when you come back.
 *
 * Who sees the row menu is `is_owner`, exactly as the web client decides it —
 * computed server-side (org admin / creator / assigned project admin) and
 * treated as owner when absent, so behaviour only tightens once every
 * deployment sends it. api-v2's `require_action` is the authority either way.
 */
import { useState } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import type { ProjectsState } from "../hooks/useProjects";
import { ProjectModal, type ProjectDraft } from "../components/projects/ProjectModal";
import { DeleteProjectModal } from "../components/projects/DeleteProjectModal";
import { ProjectMembersModal } from "../components/projects/ProjectMembersModal";
import { ProjectActionsMenu, type ProjectAction } from "../components/projects/ProjectActionsMenu";
import type { Project } from "../lib/projects";
import { LoadingBlock, Spinner } from "../components/settings/Spinner";

function FolderIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
    </svg>
  );
}

/**
 * Dates come back as ISO strings. Rendered in the viewer's locale rather than a
 * fixed format, and defensively — a malformed date must not take the pane down.
 */
function formatDate(iso: string, locale: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleDateString(locale === "zh" ? "zh-TW" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function ProjectsPane({
  projects: state,
  selectedProjectId,
  onSelectProject,
  onReload,
  canCreate,
  onCreate,
  onUpdate,
  onDelete,
  canManageMembers,
  orgId,
  token,
  selfUserId,
}: {
  /** Fetched by `AppShell` — the chat needs the same list to name the selection. */
  projects: ProjectsState;
  selectedProjectId: string | null;
  /**
   * Open a project: bind it and go to its chat. The row is the control, as in
   * the web client (`onProjectClick` → `/chat`) and in Claude — a project is a
   * place you enter, not a checkbox.
   */
  onSelectProject: (projectId: string) => void;
  /** Re-runs the fetch. A failed load must not need the app restarted. */
  onReload: () => void;
  /** `project:create` — the web client gates the same control on it. */
  canCreate: boolean;
  onCreate: (draft: ProjectDraft) => Promise<void>;
  onUpdate: (projectId: string, draft: ProjectDraft) => Promise<void>;
  onDelete: (projectId: string) => Promise<void>;
  /** `project:manage_members` — gates the Members item only, as in the web client. */
  canManageMembers: boolean;
  orgId: string | null;
  token: string | null;
  /** Excluded from bulk removal, so nobody can strip their own assignment. */
  selfUserId: string | null;
}) {
  const { t, locale } = useLocale();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Project | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Project | null>(null);
  const [managing, setManaging] = useState<Project | null>(null);

  /**
   * The row's actions, in the web client's order: Edit, Members, Delete.
   *
   * Edit and Delete are not permission-gated here for the same reason they are
   * not there — `is_owner` already decides whether the menu appears at all, and
   * the API enforces the write regardless.
   */
  const actionsFor = (project: Project): ProjectAction[] => {
    const actions: ProjectAction[] = [
      {
        key: "edit",
        label: t("projects.edit"),
        icon: <PencilIcon />,
        onSelect: () => setEditing(project),
      },
    ];
    if (canManageMembers) {
      actions.push({
        key: "members",
        label: t("projects.members"),
        icon: <UsersIcon />,
        onSelect: () => setManaging(project),
      });
    }
    actions.push({
      key: "delete",
      label: t("projects.delete"),
      icon: <TrashIcon />,
      onSelect: () => setDeleting(project),
      danger: true,
    });
    return actions;
  };

  // Absent means owner: create/update responses omit the field, so a freshly
  // created project must not lose its own menu until the next list fetch.
  const isOwner = (project: Project) => project.is_owner !== false;

  const all = state.kind === "loaded" ? state.projects : [];
  const query = search.trim().toLowerCase();
  const visible = query
    ? all.filter((p) =>
        [p.name, p.description, p.notes]
          .filter((v): v is string => typeof v === "string" && v.length > 0)
          .some((v) => v.toLowerCase().includes(query)),
      )
    : all;

  /** The web client's three-way empty state — filtered, creatable, assigned. */
  const emptyState = query
    ? { title: t("projects.noMatchingProjects"), body: t("projects.adjustFilter") }
    : canCreate
      ? { title: t("projects.noProjects"), body: t("projects.createFirstProject") }
      : { title: t("projects.noProjectsAssigned"), body: t("projects.contactProjectAdmin") };

  return (
    <section data-pane="projects">
      <div className="pane-head">
        <div className="pane-head-title">
          <h2>{t("projects.projectList")}</h2>
          {state.kind === "loading" ? <Spinner /> : null}
          {state.kind === "loaded" ? (
            <span className="pane-head-count">
              {t("projects.projectCount", { count: all.length })}
            </span>
          ) : null}
        </div>
        {canCreate && state.kind === "loaded" ? (
          <button type="button" className="settings-btn" onClick={() => setCreating(true)}>
            <PlusIcon />
            {t("projects.createProject")}
          </button>
        ) : null}
      </div>

      {/* The web client's search bar sits above the list card, and is shown
          whether or not the list is empty. */}
      {state.kind === "loaded" ? (
        <div className="search-field projects-search">
          <span className="search-field-icon">
            <SearchIcon />
          </span>
          <input
            className="settings-input"
            placeholder={t("projects.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      ) : null}

      {state.kind === "idle" ? (
        <div className="settings-card">
          <p className="settings-note">{t("projects.noOrg")}</p>
        </div>
      ) : state.kind === "loading" ? (
        <LoadingBlock size={40} label={t("projects.loadingProjects")} />
      ) : state.kind === "error" ? (
        <div className="settings-card">
          <div className="projects-error">
            <span className="projects-error-icon">
              <BuildingIcon />
            </span>
            <p className="projects-error-title">{t("projects.loadProjectsFailed")}</p>
            <p className="projects-error-detail">{state.message}</p>
            <button type="button" className="settings-btn secondary is-danger" onClick={onReload}>
              {t("projects.retry")}
            </button>
          </div>
        </div>
      ) : visible.length === 0 ? (
        <div className="settings-card is-muted">
          <div className="projects-empty">
            <span className="projects-empty-icon">
              <PlusIcon />
            </span>
            <h3>{emptyState.title}</h3>
            <p>{emptyState.body}</p>
          </div>
        </div>
      ) : (
        <div className="settings-card">
          <div className="members-scroll">
            <table className="members-table projects-table">
              <thead>
                <tr>
                  <th>{t("projects.columnName")}</th>
                  {/* Below the tablet cutoff only name, status and actions stay
                      (owner, 2026-08-25): five columns in 720px is a table nobody can read. */}
                  <th className="col-description">{t("projects.columnDescription")}</th>
                  <th>{t("projects.columnStatus")}</th>
                  <th className="col-created">{t("projects.columnCreatedAt")}</th>
                  <th className="col-actions">{t("projects.columnActions")}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((project: Project) => (
                  <tr
                    key={project.id}
                    className={`is-selectable${project.id === selectedProjectId ? " is-selected" : ""}`}
                    aria-selected={project.id === selectedProjectId}
                    tabIndex={0}
                    title={t("projects.openChat")}
                    onClick={() => onSelectProject(project.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelectProject(project.id);
                      }
                    }}
                  >
                    <td>
                      <div className="project-name-cell">
                        <span className="project-icon">
                          <FolderIcon />
                        </span>
                        <span className="project-name-text">
                          <span className="project-name" title={project.name}>
                            {project.name}
                          </span>
                          {project.notes ? (
                            <span
                              className="project-notes"
                              title={t("projects.notes", { notes: project.notes })}
                            >
                              {t("projects.notes", { notes: project.notes })}
                            </span>
                          ) : null}
                        </span>
                        {project.id === selectedProjectId ? (
                          <span className="project-selected-tag">{t("projects.selected")}</span>
                        ) : null}
                      </div>
                    </td>
                    <td
                      className="project-description col-description"
                      title={project.description || undefined}
                    >
                      {project.description || t("projects.noDescription")}
                    </td>
                    <td>
                      <span className={`status-pill${project.status ? " is-active" : ""}`}>
                        {project.status
                          ? t("projects.statusActive")
                          : t("projects.statusInactive")}
                      </span>
                    </td>
                    <td className="project-date col-created">{formatDate(project.created_at, locale)}</td>
                    <td className="col-actions">
                      {isOwner(project) ? (
                        <ProjectActionsMenu actions={actionsFor(project)} />
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ProjectModal
        open={creating}
        mode="create"
        onClose={() => setCreating(false)}
        onSave={onCreate}
      />
      <ProjectMembersModal
        project={managing}
        orgId={orgId}
        token={token}
        selfUserId={selfUserId}
        onClose={() => setManaging(null)}
      />
      <DeleteProjectModal
        project={deleting}
        onClose={() => setDeleting(null)}
        onDelete={onDelete}
      />
      <ProjectModal
        open={editing !== null}
        mode="edit"
        project={editing}
        onClose={() => setEditing(null)}
        onSave={(draft) => onUpdate(editing?.id ?? "", draft)}
      />
    </section>
  );
}
